import { Dialog } from '@components/dialog';
import { RadioGroupField, RadioItem } from '@components/radioButton/RadioGroupField';
import { ToastType } from '@components/toast';
import { PackageTypeId } from '@models/document';
import { QuoteType, TQuoteApiResponse } from '@models/quotes';
import { writingCompanyIds } from '@models/productOffering';
import { Box, Typography } from '@mui/material';
import { useCreateDocumentGeneration } from '@services/document';
import { useGetPolicy } from '@services/policy';
import { getFormattedPolicyNumber } from '@utils/policy';
import { useSnackbar } from 'notistack';
import { useState, useMemo } from 'react';

type GenerateDocumentDialogProps = {
  open: boolean;
  quote: TQuoteApiResponse;
  handleCloseDialog: () => void;
};

type DocumentOption = {
  label: string;
  packageTypeId: string;
  requiresPolicyId: boolean;
  requiresPolicyTransactionId: boolean;
};

export const GenerateDocumentDialog = ({ open, quote, handleCloseDialog }: GenerateDocumentDialogProps) => {
  const [selectedDocument, setSelectedDocument] = useState<string>('');
  const { policy } = useGetPolicy({ policyId: quote.policyId });
  const { createDocumentGenerationAsync, isPending: isLoading } = useCreateDocumentGeneration();
  const { enqueueSnackbar } = useSnackbar();

  const formattedPolicyNumber = policy
    ? getFormattedPolicyNumber(policy.policyNumberPrefix, policy.policyNumber.policyNumber)
    : `#${quote.policyNumber?.policyNumber}`;

  // Determine writing company type
  const isLSIC = quote.writingCompany?.id === writingCompanyIds.specialty;
  const isLIC = quote.writingCompany?.id === writingCompanyIds.admitted;

  // Build document options based on quote type and writing company
  const documentOptions = useMemo((): Record<string, DocumentOption> => {
    const createDocumentOption = (label: string, packageTypeId: string): DocumentOption => ({
      label,
      packageTypeId,
      requiresPolicyId: true,
      requiresPolicyTransactionId: true,
    });

    // Define document types by writing company
    const writingCompanyOptions: Record<string, DocumentOption> = {};

    if (isLSIC) {
      writingCompanyOptions.binder = createDocumentOption('Binder', PackageTypeId.Binder);
    }

    if (isLIC) {
      writingCompanyOptions.confirmationOfCoverage = createDocumentOption(
        'Confirmation of Coverage',
        PackageTypeId.Binder,
      );
    }

    // Define quote-type specific documents
    const quoteTypeDocuments: Partial<Record<QuoteType, Record<string, DocumentOption>>> = {
      [QuoteType.NewBusiness]: {
        ...writingCompanyOptions,
        policyDocument: createDocumentOption('Policy Document', PackageTypeId.Policy),
      },
      [QuoteType.Endorsement]: {
        ...writingCompanyOptions,
        endorsementDocument: createDocumentOption('Endorsement Document', PackageTypeId.Endorsement),
      },
      [QuoteType.Renewal]: {
        ...writingCompanyOptions,
        policyDocument: createDocumentOption('Renewal Document', PackageTypeId.Policy),
      },
      [QuoteType.Rewrite]: {
        ...writingCompanyOptions,
        policyDocument: createDocumentOption('Rewrite Document', PackageTypeId.Policy),
      },
    };

    return quoteTypeDocuments[quote.quoteType] || {};
  }, [quote.quoteType, isLSIC, isLIC]);

  const radioOptions: RadioItem[] = Object.entries(documentOptions).map(([key, option]) => ({
    value: key,
    label: option.label,
  }));

  const handleSubmit = async () => {
    const selectedOption = documentOptions[selectedDocument];
    if (!selectedOption) {
      enqueueSnackbar('Please select a document type', {
        preventDuplicate: true,
        variant: ToastType.Error,
      });
      return;
    }

    const policyTransactionId = quote.policyTransaction?.id ?? undefined;

    try {
      await createDocumentGenerationAsync(
        {
          quoteId: quote.id,
          packageTypeId: selectedOption.packageTypeId,
          policyId: selectedOption.requiresPolicyId ? quote.policyId : undefined,
          policyTransactionId:
            selectedOption.requiresPolicyTransactionId && policyTransactionId ? policyTransactionId : undefined,
        },
        {
          onSuccess: () => {
            enqueueSnackbar('Document generation has been queued successfully', {
              preventDuplicate: true,
              variant: ToastType.Success,
            });
            handleCloseDialog();
          },
          onError: () => {
            enqueueSnackbar('Document generation failed. Please try again.', {
              preventDuplicate: true,
              variant: ToastType.Error,
            });
          },
        },
      );
    } catch (error) {
      enqueueSnackbar('Document generation failed. Please try again.', {
        preventDuplicate: true,
        variant: ToastType.Error,
      });
    }
  };

  return (
    <Dialog
      open={open}
      title={`Generate Documents for ${formattedPolicyNumber}`}
      showBorders={false}
      handleCloseDialog={handleCloseDialog}
      primaryBtnText='Generate Selected Document'
      isPrimaryBtnLoading={isLoading}
      primaryBtnAction={handleSubmit}
      isPrimaryBtnDisabled={!selectedDocument}
      secondaryBtnText='Exit'
      secondaryBtnAction={handleCloseDialog}
      sxProps={{
        dialogContent: { display: 'flex-start' },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', padding: '20px', gap: 2 }}>
        <Typography variant='bodySmall'>
          Use this to generate forms that have not generated when they should have. Please do not use this if the form
          is already generated - it can be regenerated from the policy page with more precision.
        </Typography>

        {radioOptions.length > 0 ? (
          <RadioGroupField
            inputLabel="Select the Document you'd like to generate"
            required
            options={radioOptions}
            value={selectedDocument}
            onChange={(e) => setSelectedDocument(e.target.value)}
            layout='vertical'
            sxProps={{
              label: {
                marginLeft: '-10px',
              },
            }}
          />
        ) : (
          <Typography variant='bodySmall' color='error'>
            No document options available for this quote type and writing company combination.
          </Typography>
        )}
      </Box>
    </Dialog>
  );
};
